/**
 * Kvalitetsgrind för programmatiska sidor.
 *
 * Syfte: aldrig publicera (eller indexera) en sida som Google kan tolka som
 * "scaled content abuse" — dvs mallsidor utan eget värde. Varje programmatisk
 * sidfamilj kör sin data genom denna modul innan den får:
 *   1) index-tillstånd i <head> (annars noindex, follow)
 *   2) plats i sitemap.xml
 *
 * Grinden är avsiktligt hård: hellre 300 starka sidor än 3 000 svaga.
 */

export const QUALITY_THRESHOLDS = {
  /** Minsta mängd unik brödtext (ord) som sidan har utöver navigation/boilerplate. */
  minWords: 320,
  /** Minsta antal konkreta datapunkter (siffror: pris, vikt, mått, avstånd, år...). */
  minDataPoints: 6,
  /** Max tillåten likhet mot närmaste "syskonsida" i samma familj (0–1). */
  maxSimilarity: 0.7,
} as const;

export interface QualityAssessment {
  words: number;
  dataPoints: number;
  /** Högsta likhet mot en syskonsida, 0–1. */
  similarity: number;
  passed: boolean;
  reasons: string[];
}

const WORD_RE = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;

export function countWords(text: string): number {
  return (text.match(WORD_RE) ?? []).length;
}

/**
 * Räknar unika konkreta datapunkter. Samma tal som upprepas räknas en gång —
 * det är variationen av fakta som gör sidan unik, inte hur ofta den nämns.
 */
export function countDataPoints(text: string): number {
  const nums = text.match(/\d[\d\s.,]*/g) ?? [];
  const norm = nums.map((n) => n.replace(/[\s.,]/g, "")).filter((n) => n.length > 0);
  return new Set(norm).size;
}

function shingles(text: string, size = 5): Set<string> {
  const words = (text.toLowerCase().match(WORD_RE) ?? []);
  const out = new Set<string>();
  for (let i = 0; i + size <= words.length; i++) {
    out.add(words.slice(i, i + size).join(" "));
  }
  return out;
}

/** Jaccard-likhet på 5-gram. 1 = identisk text, 0 = inget överlapp. */
export function similarity(a: string, b: string): number {
  const sa = shingles(a);
  const sb = shingles(b);
  if (sa.size === 0 || sb.size === 0) return 0;
  let inter = 0;
  for (const s of sa) if (sb.has(s)) inter++;
  const union = sa.size + sb.size - inter;
  return union === 0 ? 0 : inter / union;
}

export function maxSimilarity(text: string, peers: string[]): number {
  let max = 0;
  for (const p of peers) {
    const s = similarity(text, p);
    if (s > max) max = s;
    if (max >= 0.999) break;
  }
  return max;
}

/**
 * Bedömer en sida utifrån dess egen unika text + syskonsidornas text.
 * `peers` ska vara texten från andra sidor i SAMMA familj (t.ex. övriga
 * årsmodeller) — det är där dubblettrisken finns.
 */
export interface QualityOverrides {
  /** Höj/sänk ordkravet för familjer med annan naturlig längd (t.ex. ordlista). */
  minWords?: number;
  minDataPoints?: number;
  maxSimilarity?: number;
}

export function assessPage(
  text: string,
  peers: string[] = [],
  overrides: QualityOverrides = {},
): QualityAssessment {
  const minWords = overrides.minWords ?? QUALITY_THRESHOLDS.minWords;
  const minDataPoints = overrides.minDataPoints ?? QUALITY_THRESHOLDS.minDataPoints;
  const maxSim = overrides.maxSimilarity ?? QUALITY_THRESHOLDS.maxSimilarity;
  const words = countWords(text);
  const dataPoints = countDataPoints(text);
  const sim = maxSimilarity(text, peers);
  const reasons: string[] = [];
  if (words < minWords) reasons.push(`för tunn text (${words} ord < ${minWords})`);
  if (dataPoints < minDataPoints)
    reasons.push(`för få datapunkter (${dataPoints} < ${minDataPoints})`);
  if (sim > maxSim) reasons.push(`för lik syskonsida (${Math.round(sim * 100)}% > ${maxSim * 100}%)`);
  return { words, dataPoints, similarity: sim, passed: reasons.length === 0, reasons };
}

/**
 * Meta-taggar för <head>. Sidor som inte klarar grinden får noindex men
 * behåller follow — länkvärdet flyter vidare till hubbsidan, och sidan
 * finns kvar för besökare som hittar den internt.
 */
export function robotsMeta(assessment: QualityAssessment): Array<{ name: string; content: string }> {
  return assessment.passed
    ? [{ name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" }]
    : [{ name: "robots", content: "noindex, follow" }];
}

/** True när sidan får ligga i sitemap.xml. */
export function sitemapEligible(assessment: QualityAssessment): boolean {
  return assessment.passed;
}

/**
 * Klipper en meta-beskrivning vid ordgräns så Google inte trunkerar mitt i ett ord.
 * ~158 tecken är den praktiska gränsen i SERP på desktop.
 */
export function clampDescription(text: string, max = 158): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 60 ? lastSpace : max).replace(/[\s,;:—-]+$/, "")}…`;
}

/**
 * Bygger en SERP-säker titel. Google visar ~60 tecken (580 px) på desktop.
 * Varumärkessuffixet läggs bara på när det ryms — annars klipps titeln vid
 * ordgräns utan suffix, så nyckelorden alltid syns i sökresultatet.
 */
export function buildTitle(base: string, brand = "AK Husbil & Fritid", max = 62): string {
  // Ta bort ett redan påklistrat varumärkessuffix så vi aldrig dubblerar det.
  const clean = base
    .replace(/\s*\|\s*AK Husbil(?: & Fritid)?(?:,\s*Kalix)?\s*$/i, "")
    .replace(/\s+/g, " ")
    .trim();
  const withBrand = `${clean} | ${brand}`;

  if (withBrand.length <= max) return withBrand;
  if (brand === "AK Husbil & Fritid") {
    const shortBrand = `${clean} | AK Husbil`;
    if (shortBrand.length <= max) return shortBrand;
  }
  if (clean.length <= max) return clean;
  // Klipp helst vid en naturlig gräns (tankstreck, kolon, komma) så titeln
  // inte slutar mitt i en mening — annars vid närmaste ordgräns.
  const cut = clean.slice(0, max);
  const breakPoint = Math.max(
    cut.lastIndexOf(" — "),
    cut.lastIndexOf(" – "),
    cut.lastIndexOf(" - "),
    cut.lastIndexOf(": "),
    cut.lastIndexOf(", "),
  );
  if (breakPoint > 46) return cut.slice(0, breakPoint).trim();
  const lastSpace = cut.lastIndexOf(" ");
  let out = cut.slice(0, lastSpace > 34 ? lastSpace : max).replace(/[\s,;:—–-]+$/, "");
  // Ta bort hängande småord i slutet ("… och", "… i") så titeln läser färdigt.
  const dangling =
    /\s+(i|på|och|med|som|för|till|av|en|ett|den|det|du|vi|så|från|om|är|vad|hur|när|utan|men|eller|åker|blir|gör|över|under|efter|inför|mot|vid|genom|hos|mellan|per)$/i;
  while (dangling.test(out)) out = out.replace(dangling, "");
  out = out.replace(/\s+\d[\d\s]*$/, "");
  // Öppen parentes utan avslut ser trasigt ut i SERP – klipp bort resten.
  if ((out.match(/\(/g)?.length ?? 0) > (out.match(/\)/g)?.length ?? 0)) {
    out = out.slice(0, out.lastIndexOf("(")).trim();
  }
  while (dangling.test(out)) out = out.replace(dangling, "");
  return out.replace(/[\s,;:—–&+/-]+$/, "");
}
